import { Layout } from 'react-grid-layout';
import { isEmpty } from 'lodash';
import { ArdoqId, WidgetTypes } from '@ardoq/api-types';
import {
  DashboardFilterParameter,
  DashboardFiltersCommands,
  TrackEvent,
  WidgetsCommands,
} from './types';
import { getEditModeWidgetProps } from './widgets/getEditModeWidgetProps';
import { getAddWidgetButtonProps } from './AddWidgetButtons';
import { getLayout, BreakpointKeys } from './layoutUtils';
import { DashboardBuilderFilterIslandProps } from './filter/DashboardFilterIsland';
import { Locale } from '@ardoq/locale';
import { DashboardFilterValues, HexColor } from '@ardoq/api-types';
import { DashboardColor } from './types';
import { DashboardTrackingEventsNames } from './tracking';
import { DashboardBuilderWidgetsProps } from './widgets/types';
import { EditModeDashboardProps } from './widgets/editModeTypes';
import type { I18nLocale } from '@ardoq/i18n';

type GetEditModeDashboardPropsArgs = {
  // Direct pass-through props
  dashboardId: ArdoqId | undefined;
  dashboardFilterIslandProps: DashboardBuilderFilterIslandProps;
  description: string | undefined;
  selectedWidgetId: string | null;
  trackEvent: TrackEvent;
  isInLinkDashboardFiltersMode: boolean;
  dashboardFiltersCommands: DashboardFiltersCommands;
  widgetsCommands: WidgetsCommands;
  // Props for computing derived values
  widgets: DashboardBuilderWidgetsProps[];
  dashboardFilterValues: DashboardFilterValues;
  dashboardFilterParameters: DashboardFilterParameter[];
  linkModeDashboardFilterParameterId: string | null;
  settings: { colors: Array<DashboardColor> };
  locale: Locale;
  isLoading: boolean;
  isSaving: boolean;
  i18nLocale: I18nLocale;
  showPowerBiIframeWidget: boolean;
};

export const getEditModeDashboardProps = ({
  dashboardFilterIslandProps,
  description,
  locale,
  selectedWidgetId,
  trackEvent,
  isInLinkDashboardFiltersMode,
  dashboardFiltersCommands,
  widgetsCommands,
  widgets,
  dashboardFilterValues,
  dashboardFilterParameters,
  dashboardId,
  linkModeDashboardFilterParameterId,
  isSaving,
  isLoading,
  settings,
  i18nLocale,
  showPowerBiIframeWidget,
}: GetEditModeDashboardPropsArgs): EditModeDashboardProps => ({
  dashboardFilterIslandProps,
  description,
  isLoading,
  selectedWidgetId,
  trackEvent,
  isInLinkDashboardFiltersMode,
  widgetsCommands,
  widgetProps: widgets.map(widget =>
    getEditModeWidgetProps({
      dashboardId,
      widget,
      dashboardFilterValues,
      dashboardFilterParameters,
      selectedWidgetId,
      isInLinkDashboardFiltersMode,
      linkModeDashboardFilterParameterId,
      widgetsCommands,
      isSaving,
      trackEvent,
      dashboardFilterCommands: dashboardFiltersCommands,
      colors: settings.colors.reduce<Record<string, HexColor>>(
        (colorMap, { dataPoint, color }) => {
          colorMap[dataPoint] = color;
          return colorMap;
        },
        {}
      ),
      userLocale: locale,
      i18nLocale,
    })
  ),
  trackOnDragStart: (_: Layout[], draggedLayout: Layout) => {
    const draggedWidgetType = widgets.find(
      widget => widget._id === draggedLayout.i
    )?.viewType;
    trackEvent(
      draggedWidgetType === WidgetTypes.HEADER
        ? DashboardTrackingEventsNames.DRAGGED_HEADER_IN_DASHBOARD_BUILDER
        : DashboardTrackingEventsNames.DRAGGED_CHART_IN_DASHBOARD_BUILDER
    );
  },
  addWidgetButtonProps: getAddWidgetButtonProps(
    widgets,
    widgetsCommands,
    isEmpty(dashboardFilterValues),
    showPowerBiIframeWidget
  ),
  getLayoutForCurrentBreakpoint: (
    currentBreakpoint: BreakpointKeys,
    isDragging: boolean
  ) =>
    getLayout(
      currentBreakpoint,
      isDragging,
      widgets,
      isSaving,
      isInLinkDashboardFiltersMode
    ),
});
